import { useState } from 'react';

import { usePaymentsAdmin } from './usePaymentsAdmin';

const STATUS_LABELS: Record<string, string> = {
  pending: 'ждёт оплаты',
  paid: 'оплачен',
  refunding: 'возврат идёт',
  refunded: 'возвращён',
  failed: 'сбой',
  expired: 'просрочен',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

/** Выручка и список платежей с возвратом. Возврат — в два нажатия: ошибка дорогая. */
export function PaymentsAdmin() {
  const { payments, revenue, days, setDays, refund } = usePaymentsAdmin();
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <>
      <section aria-label="Выручка" className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-text">Выручка</h2>

        <div className="flex gap-2">
          {([7, 30] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={days === value}
              onClick={() => setDays(value)}
              className="rounded-lg border border-border-strong px-3 py-2 text-sm text-text aria-pressed:border-accent"
            >
              {value} дней
            </button>
          ))}
        </div>

        {revenue.isPending && <p className="text-text-2">Считаем…</p>}
        {revenue.isError && <p className="text-danger">Не удалось собрать выручку. Обновите страницу.</p>}
        {revenue.data?.rows.length === 0 && <p className="text-sm text-text-2">Платежей за этот период нет.</p>}

        {revenue.data && revenue.data.rows.length > 0 && (
          <ul className="flex flex-col gap-2">
            {revenue.data.rows.map((row) => (
              <li key={`${row.provider}:${row.packId}`} className="flex flex-wrap items-baseline justify-between gap-2 text-sm text-text">
                <span>
                  {row.provider} · <span>{row.packId}</span>
                </span>
                <span className="tabular-nums text-text-2">
                  оплат: {row.paidCount} · кредитов: {row.credits} · возвратов: {row.refundedCount}
                </span>
                <span className="tabular-nums">{`${row.amount} ${row.currency}`}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Платежи" className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-text">Платежи</h2>

        {payments.isPending && <p className="text-text-2">Загрузка…</p>}
        {payments.isError && <p className="text-danger">Не удалось загрузить платежи. Обновите страницу.</p>}
        {payments.data?.payments.length === 0 && <p className="text-sm text-text-2">Платежей пока нет.</p>}

        {refund.isError && (
          <p role="alert" className="text-sm text-danger">
            {refund.error instanceof Error ? refund.error.message : 'Не удалось вернуть оплату.'}
          </p>
        )}

        <ul className="flex flex-col gap-3">
          {payments.data?.payments.map((payment) => (
            <li key={payment.id} className="flex flex-col gap-1 rounded-card border border-border px-3 py-2 text-sm">
              <div className="flex justify-between gap-2 text-text">
                <span>{payment.packId} · {payment.credits} кредитов</span>
                <span className="tabular-nums">{`${payment.amount} ${payment.currency}`}</span>
              </div>
              <div className="flex items-center justify-between gap-2 text-text-3">
                <span>{STATUS_LABELS[payment.status] ?? payment.status} · {formatDate(payment.createdAt)}</span>

                {payment.status === 'paid' &&
                  (confirming === payment.id ? (
                    <button
                      type="button"
                      disabled={refund.isPending}
                      onClick={() => {
                        refund.mutate(payment.id);
                        setConfirming(null);
                      }}
                      className="rounded-lg border border-danger px-3 py-1 text-danger disabled:opacity-40"
                    >
                      Точно вернуть?
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirming(payment.id)}
                      className="btn btn-secondary px-3 py-1"
                    >
                      Вернуть
                    </button>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
